<template>
  <fn-link
    :id="contentTile.tracking_id || undefined"
    :to="isNuxtLink(contentTile.link.url)"
    :href="isHrefLink(contentTile.link.url)"
    :target="contentTile.link?.target"
    class="content-tile-double"
    :class="{
      'content-tile-double--static': !contentTile.link,
      'content-tile-double--text-overlay': contentTile.display_group !== 'empty',
    }"
    :static="!contentTile.link"
    no-arrow
  >
    <fn-picture
      v-if="contentTile.background_image"
      class="ctd-image"
      :src="contentTile.background_image.url"
      :width="contentTile.background_image.width"
      :height="contentTile.background_image.height"
      :alt="contentTile.background_image.alt"
      :title="contentTile.background_image.title"
    />
    <div
      v-if="contentTile.display_group !== 'empty'"
      class="cdt-text-overlay-wrapper"
    >
      <div class="ctd-text-overlay">
        <fn-curve class="to-curve" />

        <div class="to-content">
          <span
            v-if="contentTile.badge"
            class="ctd-badge"
          >
            {{ contentTile.badge }}
          </span>
          <span
            v-if="contentTile.logo"
            class="ctd-logo"
          >
            <fn-picture
              :src="contentTile.logo.url"
              :width="contentTile.logo.width"
              :height="contentTile.logo.height"
              :alt="contentTile.logo.alt"
              :title="contentTile.logo.title"
            />
          </span>
          <fn-headline
            v-if="
              (contentTile.display_group === 'headline_copy' || contentTile.display_group === 'headline_link') &&
              contentTile.headline
            "
            class="ctd-headline"
            :color="contentTile.headline_color"
            type="div"
            size="h3"
          >
            {{ contentTile.headline }}
          </fn-headline>
          <div
            v-if="contentTile.display_group === 'headline_copy' && contentTile.copy"
            class="ctd-copy"
          >
            {{ contentTile.copy }}
          </div>
          <fn-list
            v-if="contentTile.display_group === 'bullet_points' && contentTile.bullet_points"
            class="ctd-bullets"
            unstyled
          >
            <li
              v-for="(point, index) in contentTile.bullet_points"
              :key="index"
              class="b-item"
            >
              <fn-icon
                class="b-icon"
                icon="circle-tick"
                color="green-dark"
                size="xs"
              />
              <span class="b-text">
                {{ point.content }}
              </span>
            </li>
          </fn-list>
          <fn-link
            v-if="contentTile.display_group === 'headline_link' && contentTile.link"
            class="ctd-link"
            color="orientation-green"
            static
            no-arrow
            large
            bold
            underline
          >
            {{ contentTile.link.title }}
          </fn-link>
        </div>
      </div>
    </div>
  </fn-link>
</template>

<script>
import linkHandler from '@/mixins/linkHandler';

export default {
  mixins: [linkHandler],
  props: {
    contentTile: {
      type: Object,
      required: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.content-tile-double {
  display: block;
  position: relative;
  width: 100%;
  min-height: 300px;
  grid-row: span 20;
  height: 100%;
  overflow: hidden;
  border-radius: map-get($border-radiuses, 'xl');
  transition: box-shadow 0.3s ease-in-out;

  &:hover {
    box-shadow: 0 10px 20px rgba(palette(black), 0.3);
  }

  &.content-tile-double--static {
    cursor: default;

    &:hover {
      box-shadow: none;
    }
  }

  &.content-tile-double--text-overlay {
    border: 1px solid palette(grey, lighter);
  }

  .ctd-image :deep(.picture-img) {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -1;
  }

  .cdt-text-overlay-wrapper {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background: $color-general-background-neutral-default;
    box-sizing: border-box;

    .ctd-text-overlay {
      position: relative;

      .to-curve {
        position: absolute;
        bottom: calc(100% - 1px);
        width: calc(100% + 2px);
        left: -1px;
        z-index: -1;
      }

      .to-content {
        padding: $pad * 2.5 $pad $pad $pad;
      }

      .ctd-badge {
        position: absolute;
        top: math.div($pad, 2);
        left: $pad;
        padding: math.div($pad, 8) math.div($pad, 2);
        background-color: palette(grey, metallic);
        border-radius: map-get($border-radiuses, 'l');
        color: $color-general-text-inverse-default;
        text-transform: uppercase;
        font-size: map-get($font-sizes, 's');
        font-weight: map-get($font-weights, bold);
      }

      .ctd-logo {
        position: absolute;
        bottom: calc(100% - #{math.div($pad, 2)} - 22px);
        right: $pad;
        width: 80px;
        height: 80px;
        background-color: $color-general-background-neutral-default;
        padding: math.div($ui-height, 4);
        border-radius: map-get($border-radiuses, 'xl');
        box-shadow:
          0 4px 10px rgba(palette(black), 0.06),
          0 1px 5px rgba(palette(black), 0.06);
      }

      .ctd-headline,
      .ctd-copy {
        margin-bottom: math.div($pad, 2);
      }

      .ctd-copy {
        color: palette(grey, dark);
        font-weight: map-get($font-weights, bold);
      }

      .ctd-bullets {
        row-gap: math.div($pad, 2);
        margin-bottom: math.div($pad, 2);

        .b-item {
          display: flex;

          .b-icon {
            margin-right: math.div($pad, 2);
          }

          .b-text {
            font-size: map-get($font-sizes, 'm');
            color: palette(grey, dark);
            font-weight: map-get($font-weights, bold);
          }
        }
      }
      .ctd-link:hover {
        color: palette(green, base);
      }
    }
  }
}
</style>
